import { useState } from "react";
import { Database, Save, Star, Tag, ChevronRight, TrendingUp, BookOpen, Recycle, Warehouse, Timer, AlertTriangle, Trash2, EyeOff, Wrench, FileSpreadsheet, Boxes, PackageSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";



export default function DataManagement() {
  const { t } = useTranslation("admin");
  const { isSuperadmin: isAdmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();


  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings" as any)
        .select("*");
      if (error) throw error;
      return data as any[];
    },
  });

  // ===== Obsolete `hourly_rates` row cleanup =====
  // Hourly rates moved to per-custom-role on `custom_roles.hourly_rate`.
  // Detect any leftover `platform_settings` row and offer Disable / Delete.
  const obsoleteRow = settings?.find((s: any) => s.setting_key === "hourly_rates");
  const obsoleteIsDisabled =
    obsoleteRow &&
    typeof obsoleteRow.setting_value === "object" &&
    obsoleteRow.setting_value !== null &&
    (obsoleteRow.setting_value as any).disabled === true;
  const [showDeleteObsolete, setShowDeleteObsolete] = useState(false);

  const disableObsoleteMutation = useMutation({
    mutationFn: async () => {
      const previous = obsoleteRow?.setting_value ?? [];
      const { error } = await supabase
        .from("platform_settings" as any)
        .update({
          setting_value: { disabled: true, archived_at: new Date().toISOString(), previous_value: previous } as any,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("setting_key", "hourly_rates");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
      toast({ title: "Obsolete setting disabled", description: "The legacy hourly_rates row was archived." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to disable", description: err.message, variant: "destructive" });
    },
  });

  const deleteObsoleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("platform_settings" as any)
        .delete()
        .eq("setting_key", "hourly_rates");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
      setShowDeleteObsolete(false);
      toast({ title: "Obsolete setting deleted" });
    },
    onError: (err: any) => {
      setShowDeleteObsolete(false);
      toast({ title: "Failed to delete", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("data.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("data.subtitle")}</p>
      </div>

      <div className="px-6 space-y-6">
        {/* Obsolete `hourly_rates` row warning */}
        {obsoleteRow && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">
                Obsolete setting detected:{" "}
                <code className="text-xs bg-amber-500/20 text-foreground px-1.5 py-0.5 rounded">hourly_rates</code>
                {obsoleteIsDisabled && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">(already disabled)</span>
                )}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Hourly rates are now stored per custom role under{" "}
                <button
                  onClick={() => navigate("/settings/users")}
                  className="underline hover:no-underline font-medium text-foreground"
                >
                  User &amp; Role Management
                </button>
                . The legacy <code className="text-xs">platform_settings.hourly_rates</code> row is no longer read by the app and can be safely cleaned up.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {!obsoleteIsDisabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => disableObsoleteMutation.mutate()}
                    disabled={disableObsoleteMutation.isPending}
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                    {disableObsoleteMutation.isPending ? "Disabling…" : "Disable (archive value)"}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setShowDeleteObsolete(true)}
                  disabled={deleteObsoleteMutation.isPending}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete row
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* Appliance Categories */}
        <button
          onClick={() => navigate("/platform/data/appliance-categories")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Boxes className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("settings.sections.applianceCategories.title")}</p>
            <p className="text-sm text-muted-foreground">{t("settings.sections.applianceCategories.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Recommendation Engine */}
        <button
          onClick={() => navigate("/platform/data/recommendation-engine")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">Recommendation Engine</p>
            <p className="text-sm text-muted-foreground">Upload and activate the harvest recommendation sheet.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Spare Part Configuration */}
        <button
          onClick={() => navigate("/platform/data/spare-parts")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("data.tiles.spareParts.title")}</p>
            <p className="text-sm text-muted-foreground">{t("data.tiles.spareParts.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {[
          { to: "/platform/data/brand-harvest-defaults", title: "Brand harvest defaults", desc: "Default components to harvest per brand and appliance type.", Icon: Recycle },
          { to: "/platform/data/legacy-parts", title: t("legacyParts.title"), desc: t("legacyParts.description"), Icon: PackageSearch },
          { to: "/platform/data/grade-pricing", title: "Grade Pricing Defaults", desc: "Default A/B/C resale % used by companies without their own override.", Icon: Star },
        ].map((c) => (
          <button
            key={c.to}
            onClick={() => navigate(c.to)}
            className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <c.Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{c.title}</p>
              <p className="text-sm text-muted-foreground">{c.desc}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        ))}

        {[
          { to: "/platform/data/appliance-prices", key: "appliancePrices" },
          { to: "/platform/data/refurbished-prices", key: "refurbPrices" },
        ].map((c) => (
          <button
            key={c.key}
            onClick={() => navigate(c.to)}
            className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{t(`data.tiles.${c.key}.title`)}</p>
              <p className="text-sm text-muted-foreground">{t(`data.tiles.${c.key}.description`)}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        ))}


      </div>

      <AlertDialog open={showDeleteObsolete} onOpenChange={setShowDeleteObsolete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("data.deleteObsolete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              <span dangerouslySetInnerHTML={{ __html: t("data.deleteObsolete.body") }} />
              {obsoleteRow && Array.isArray((obsoleteRow as any).setting_value) && (obsoleteRow as any).setting_value.length > 0 && (
                <span className="block mt-2 text-foreground">
                  {t("data.deleteObsolete.archivedNote", { count: (obsoleteRow as any).setting_value.length })}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteObsoleteMutation.mutate()}
              disabled={deleteObsoleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteObsoleteMutation.isPending ? t("data.deleteObsolete.deleting") : t("data.deleteObsolete.deletePermanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
